export { captureLoginToken, hasLoginHash, readLoginToken }

// The magic-login credential travels in the URL *fragment* as `#login:<token>` — minted by
// `server/auth/buildMagicLoginLink.ts`, consumed here on the client. The fragment never
// reaches the server (browsers don't send it), so the two sides agree on this prefix by
// contract. This module owns the literal AND the capture: `captureLoginToken` / `hasLoginHash` /
// `readLoginToken` are the only interface (`+client.ts` captures, `+Page.tsx`'s hash hooks +
// `+data.client.ts`'s token exchange consume), so the prefix string lives in exactly one place.
const LOGIN_HASH_PREFIX = '#login:'

// The token pulled out of the fragment at boot. We capture it into a variable and strip the
// fragment AS EARLY AS POSSIBLE (`+client.ts`, before `Sentry.init`) so the credential never
// lingers in `window.location` where Sentry's pageload transaction — or any other reader — could
// pick it up. `null` until captured, and when the fragment was absent or carried an empty token.
let capturedToken: string | null = null

// Read the `#login:<token>` fragment into `capturedToken` and strip it from the address bar.
// Call this as the very FIRST thing in `+client.ts` — before `Sentry.init` — so the token is gone
// from the URL before anything reads `window.location`. No-op when there's no such fragment
// (returning visitors), and idempotent: a second call sees the already-stripped URL and leaves the
// captured value untouched. `replaceState` emits no `hashchange`, so this won't trip
// `useReloadOnLoginHash`.
function captureLoginToken(): void {
  const hash = window.location.hash
  if (!hash.startsWith(LOGIN_HASH_PREFIX)) return
  capturedToken = hash.slice(LOGIN_HASH_PREFIX.length) || null
  history.replaceState(null, '', window.location.pathname + window.location.search)
}

// True when the current URL STILL carries a `#login:…` fragment. Reads the LIVE hash (not the
// captured value) on purpose: `useReloadOnLoginHash` fires on a freshly pasted token that hasn't
// been captured yet, and `useStripConsumedLoginHash` re-strips if anything restored the fragment.
// A normal boot strips it in `captureLoginToken`, so this is usually false after that.
function hasLoginHash(): boolean {
  return window.location.hash.startsWith(LOGIN_HASH_PREFIX)
}

// The token to exchange for the `auth` cookie. A `#login:` fragment STILL in the URL here is always
// the freshest intent, so prefer it over `capturedToken`:
//   - Normal path (magic link clicked from email = a hard load): `captureLoginToken` already ran at
//     boot and stripped the fragment, so there's none here → we return the captured value. Unchanged.
//   - Fresh paste into an ALREADY-OPEN tab: `useReloadOnLoginHash` triggers a SOFT `reload()`, which
//     re-runs `data()` but NOT `+client`'s `captureLoginToken` — so `capturedToken` still holds an
//     earlier boot's token (or null). Reading the live fragment here exchanges the just-pasted token
//     instead of re-submitting the stale one.
// The live fragment is read only when present (a fresh paste), and `useStripConsumedLoginHash` clears
// it after render — so this adds no token exposure beyond what the user just put in the address bar.
function readLoginToken(): string | null {
  const hash = window.location.hash
  if (hash.startsWith(LOGIN_HASH_PREFIX)) return hash.slice(LOGIN_HASH_PREFIX.length) || null
  return capturedToken
}
